<template>
  <div class="rd-screen">
    <report-tools :copy="sections.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="busy" kind="loading"></report-state>
    <report-state v-else-if="!sections.length" icon="mdi-store-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <template v-else>
      <section v-for="section in sections" :key="section.key" class="mr-company">
        <div class="rd-section mr-company__head">
          <div class="mr-company__title">
            <h2>{{ section.name }}</h2>
          </div>
          <span class="rd-num">{{ section.summary }}</span>
        </div>

        <div class="rd-card">
          <table class="rd-tbl">
            <thead>
              <tr>
                <th scope="col" class="rd-col-num">#</th>
                <th scope="col">{{ $t("meal") }}</th>
                <th scope="col">{{ $t("type") }}</th>
                <th v-for="shift in visibleShifts" :key="shift.id" scope="col" class="is-right">
                  <report-shift-tag :shift="shift.id"></report-shift-tag>
                </th>
                <th scope="col" class="is-right">{{ $t("total") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in section.rows" :key="row.key">
                <td class="rd-num rd-muted">{{ index + 1 }}</td>
                <td class="is-name" :title="row.name">{{ row.name }}</td>
                <td class="rd-muted">{{ row.type || "–" }}</td>
                <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num" :class="{ 'is-zero': !row[shift.key] }">
                  {{ row.text[shift.key] }}
                </td>
                <td class="is-right rd-num"><b>{{ row.total }}</b></td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td>{{ $t("total") }}</td>
                <td></td>
                <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">{{ section.sums[shift.key] }}</td>
                <td class="is-right rd-num">{{ section.total }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabRestaurantMeals from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabRestaurantMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import hasPlural from "@/components/report/shared/hasPlural";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Restorani obroci firme "više restorana" na računaru: po restoranu obroci × smena sa zbirom. Restorani, smene,
 * period i pretragu (naziv ili tip obroka) čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz
 * starog taba.
 */
export default {
  name: "CompanyDesktopMultiRestaurantMeals",
  extends: CompanyMultiRestaurantTabRestaurantMeals,
  mixins: [hasMultiFeed, hasPlural],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    busy() {
      return this.isLoading;
    },
    hasRows() {
      return this.sections.length > 0;
    },
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.shiftsWithData.includes(shift.id) &&
          (shift.id < 4 ||
            this.restaurantCards.some((card) =>
              card.meals.some((meal) => (meal[shift.key] && meal[shift.key].totalQuantity) > 0)
            ))
      );
    },
    /** Restorani sa obrocima posle pretrage (naziv ili tip obroka). */
    sections() {
      const search = this.$store.state.report.filters.search;
      return this.restaurantCards
        .map((card) => {
          const found = search ? this.customSearch(card.meals, [{ value: "name" }, { value: "type" }], search) : card.meals;
          const rows = found.map((meal, index) => {
            const row = {
              key: `${meal.id || meal.name}-${index}`,
              name: this.toLatin(meal.name || ""),
              type: this.typeText(meal.type),
              total: meal.total || 0,
              text: {},
            };
            SHIFTS.forEach((shift) => {
              row[shift.key] = (meal[shift.key] && meal[shift.key].totalQuantity) || 0;
              row.text[shift.key] = this.formatShiftQuantity(meal[shift.key]);
            });
            return row;
          });
          const sums = {};
          SHIFTS.forEach((shift) => {
            const sum = { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 };
            found.forEach((meal) => {
              const data = meal[shift.key] || {};
              sum.customerQuantity += data.customerQuantity || 0;
              sum.restaurantQuantity += data.restaurantQuantity || 0;
              sum.totalQuantity += data.totalQuantity || 0;
            });
            sums[shift.key] = this.formatShiftQuantity(sum);
          });
          const total = rows.reduce((sum, row) => sum + row.total, 0);
          return {
            key: String(card.id),
            name: card.name,
            rows,
            sums,
            total,
            summary: this.pluralT("report_mobile.meals_n", total),
          };
        })
        .filter((section) => section.rows.length);
    },
    total() {
      return this.sections.reduce((sum, section) => sum + section.total, 0);
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.total, tone: "inverse" },
        { key: "restaurants", label: this.$t("restaurants"), value: this.sections.length },
      ];
    },
  },
  methods: {
    /** Tip obroka iz izveštaja ("package", "meal", "food") prevedeno; nepoznat tip ostaje kakav je. */
    typeText(type) {
      return ["package", "meal", "food"].includes(type) ? this.$t(type) : type || "";
    },
    async copyTable() {
      const headers = [
        this.$t("restaurant"),
        this.$t("meal"),
        this.$t("type"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const rows = this.sections.flatMap((section) =>
        section.rows.map((row) => [section.name, row.name, row.type, ...this.visibleShifts.map((shift) => row.text[shift.key]), row.total])
      );
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
